import { useEffect, useRef, useState } from 'react'
import './styles.scss'
import classNames from 'classnames'

interface Props {
  productBenefits?: Array<{
    title: string | undefined
    description: string | undefined
    image: string | undefined
  }>
  variant?: 'default' | 'mobile-first-fold'
}

const LEFT_BOUNDARY_THRESHOLD = 170

export function GalleryProductBenefits({
  productBenefits,
  variant = 'default',
}: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const [isCloseToEdge, setIsCloseToEdge] = useState(false)
  const isMobileFirstFold = variant === 'mobile-first-fold'

  useEffect(() => {
    if (!ref?.current) {
      return
    }

    const isCloseToLeftBoundary =
      ref.current.getBoundingClientRect().left <= LEFT_BOUNDARY_THRESHOLD

    setIsCloseToEdge(isCloseToLeftBoundary)
  }, [])

  if (!productBenefits || productBenefits.length === 0) {
    return null
  }

  const benefitsToShow = productBenefits.slice(0, 4)

  return (
    <>
      <div
        className={classNames(
          'm-auto flex items-center justify-center col-span-6',
          {
            'gap-20 py-10': !isMobileFirstFold,
            'w-full gap-[26px] px-4 pt-[14px] pb-[10px]': isMobileFirstFold,
          }
        )}
        ref={ref}
      >
        {benefitsToShow.map((benefit, index) => {
          return (
            <div
              className={classNames(
                'gallery-product-benefit-item flex flex-col items-center relative',
                {
                  'gap-4': !isMobileFirstFold,
                  'gap-1 w-[90px]': isMobileFirstFold,
                }
              )}
              key={`${benefit?.title}-${index}`}
            >
              {benefit?.image && (
                <img
                  width={isMobileFirstFold ? 21 : 32}
                  height={isMobileFirstFold ? 21 : 32}
                  src={benefit.image}
                  alt={benefit.title}
                  className="grayscale"
                  onLoad={(event) => {
                    const image = event.currentTarget

                    if (image.naturalWidth <= 2 && image.naturalHeight <= 2) {
                      image.src =
                        'https://decathlonstoreapp.vteximg.com.br/arquivos/galleryProductBenefitDefault.svg'
                    }
                  }}
                  onError={(event) => {
                    const image = event.currentTarget

                    image.src =
                      'https://decathlonstoreapp.vteximg.com.br/arquivos/galleryProductBenefitDefault.svg'
                  }}
                />
              )}
              {benefit?.title && (
                <p
                  className={classNames('text-[#000] text-center', {
                    'text-font-size-3 leading-[120%] font-semibold':
                      !isMobileFirstFold,
                    'text-[10px] leading-[120%] font-medium': isMobileFirstFold,
                  })}
                >
                  {benefit.title}
                </p>
              )}
              {!isMobileFirstFold && benefit?.description && (
                <p
                  className={classNames(
                    'gallery-product-benefit-item-tooltip w-[320px] h-max z-[1] text-font-size-3 text-[#000] absolute py-2 px-4 bg-restructure-background-primary border border-gray top-[88px]',
                    {
                      'left-0': isCloseToEdge && index === 0,
                    }
                  )}
                >
                  {benefit.description}
                </p>
              )}
            </div>
          )
        })}
      </div>
      {isMobileFirstFold && (
        <div className="w-full h-px bg-[#DADDE1]" aria-hidden="true" />
      )}
    </>
  )
}
